import { useEffect, useState } from "react";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { useNavigate } from "react-router";
import { motion } from "framer-motion";
import Modal from "@mui/material/Modal";
import authApi from "../api/auth";
import { useAuth } from "../contexts/auth/AuthContext";

const MIN_PASSWORD_LENGTH = 8;
const LOGOUT_DELAY_MS = 3000;

const PasswordInput = ({ className, ...inputProps }) => {
  const [revealed, setRevealed] = useState(false);

  return (
    <div className="relative">
      <input className={`${className} pr-10`} type={revealed ? "text" : "password"} {...inputProps} />
      <button
        type="button"
        aria-label={revealed ? "Hide password" : "Show password"}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-xl text-icon_alt hover:text-icon"
        onClick={() => setRevealed((prev) => !prev)}
      >
        {revealed ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
      </button>
    </div>
  );
};

const ChangePasswordView = () => {
  const { authLogout } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [passwordChanged, setPasswordChanged] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!passwordChanged) return;
    const timeout = setTimeout(async () => {
      await authLogout();
      navigate("/login", { replace: true });
    }, LOGOUT_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [passwordChanged]);

  const validate = () => {
    if (currentPassword.length === 0) return "Please fill in your current password.";
    if (newPassword.length < MIN_PASSWORD_LENGTH)
      return `Your new password must be at least ${MIN_PASSWORD_LENGTH} characters long.`;
    if (newPassword === currentPassword) return "Your new password must differ from your current password.";
    if (newPassword !== confirmPassword) return "The new passwords don't match.";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validate();
    setErrorMessage(validationError);
    if (validationError) return;

    setSubmitting(true);
    try {
      await authApi.changePassword({ currentPassword, newPassword });
      setPasswordChanged(true);
      return;
    } catch (err) {
      const status = err.response?.status;
      if (status === 400 || status === 403) {
        setErrorMessage("Your current password is incorrect.");
      } else {
        setErrorMessage("Something went wrong. Please try again later.");
      }
    }
    setSubmitting(false);
  };

  const inputClass = "shadow-large rounded p-2 w-[90vw] md:w-[30vw] md:min-w-[400px]";

  return (
    <div className="flex justify-center items-center w-full h-[calc(100vh-100px)]">
      <form noValidate onSubmit={handleSubmit} className="flex flex-col gap-2 items-center justify-center w-fit">
        <div className="text-3xl font-bold mb-4">Change password</div>
        <div className="text-base text-error my-2" role="alert">
          {errorMessage}
        </div>
        <PasswordInput
          className={inputClass}
          placeholder="Current password..."
          autoComplete="current-password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
        />
        <PasswordInput
          className={inputClass}
          placeholder="New password..."
          autoComplete="new-password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
        />
        <PasswordInput
          className={inputClass}
          placeholder="Confirm new password..."
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
        />
        <button
          type="submit"
          disabled={submitting}
          className="bg-link text-txt_secondary px-8 py-2 min-w-[160px] rounded shadow-default hover:opacity-90 disabled:opacity-50 place-self-end"
        >
          Change password
        </button>
      </form>
      <Modal open={passwordChanged}>
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-2/5 min-w-[320px] outline-none text-left">
          <motion.div
            className="bg-white rounded shadow overflow-hidden"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex flex-col items-center gap-3 p-6 text-center">
              <svg viewBox="0 0 52 52" className="w-16 h-16" aria-hidden="true">
                <motion.circle
                  cx="26"
                  cy="26"
                  r="24"
                  fill="none"
                  stroke="#ACD1AF"
                  strokeWidth="3"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 0.5 }}
                />
                <motion.path
                  d="M14 27 l8 8 l16 -16"
                  fill="none"
                  stroke="#ACD1AF"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 0.4, delay: 0.4 }}
                />
              </svg>
              <h2 className="text-2xl">Password changed</h2>
              <p>
                Your password has been changed. You will be logged out in {LOGOUT_DELAY_MS / 1000} seconds. Please log
                in again with your new password.
              </p>
            </div>
            <motion.div
              className="h-1 bg-completed origin-left"
              initial={{ scaleX: 1 }}
              animate={{ scaleX: 0 }}
              transition={{ duration: LOGOUT_DELAY_MS / 1000, ease: "linear" }}
            />
          </motion.div>
        </div>
      </Modal>
    </div>
  );
};

export default ChangePasswordView;
